import { useState, type ReactNode } from 'react';
import { Button, InfoBox, Input, Modal, NativeSelect } from '@immo/ui-kit';
import {
  DOSSIER_NUMBER_STYLES,
  FACT_KEY_FREQUENCIES,
  FACT_KEY_GROUPS,
  FactValueTypeSchema,
  NEW_FACT_KEY_ID_PATTERN,
  type FactKeyFrequency,
  type FactKeyGroup,
  type FactValueType,
  type PropertyCategory,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { FormField } from '@/components/form/form-field';
import { FactKeyCategoryPicker } from '@/pages/skills/fact-key-category-picker';
import { FactKeyNeedRows } from '@/pages/skills/fact-key-need-rows';
import {
  conflictOf,
  duplicateOf,
  normaliseNeedRows,
  withoutId,
  type AmbiguousDetails,
  type DraftNeedRow,
  type NeedConflict,
} from '@/pages/skills/fact-key-needs';

const INCOMPLETE_CODE = 'property.fact-key.incomplete';
const INVALID_ID_CODE = 'property.fact-key.invalid-id';
const NEED_AMBIGUOUS_CODE = 'property.fact-key.need-ambiguous';
const ID_TAKEN_CODE = 'property.fact-key.id-taken';
const ID_HINT_ID = 'create-fact-key-id-hint';
const UNIT_MAX_LENGTH = 32;

type NumberStyle = (typeof DOSSIER_NUMBER_STYLES)[number];

type Draft = {
  readonly key: string;
  readonly label: string;
  readonly groupId: FactKeyGroup | '';
  readonly valueType: FactValueType | '';
  readonly frequency: FactKeyFrequency | '';
  readonly categories: ReadonlyArray<PropertyCategory>;
  readonly needs: ReadonlyArray<DraftNeedRow>;
  readonly numberStyle: NumberStyle;
  readonly unit: string;
};

const EMPTY_DRAFT: Draft = {
  key: '',
  label: '',
  groupId: '',
  valueType: '',
  frequency: '',
  categories: [],
  needs: [],
  numberStyle: 'GROUPED_DECIMAL',
  unit: '',
};

type Props = {
  readonly onClose: () => void;
  readonly onCreated: () => void;
};

export function CreateFactKeyDialog({ onClose, onCreated }: Props) {
  const t = useTranslations('pages.admin.skills.fact-keys');
  const tCol = useTranslations('pages.admin.skills.fact-keys.columns');
  const tErrors = useTranslations('errors');
  const [draft, setDraftState] = useState<Draft>(EMPTY_DRAFT);
  const [conflict, setConflict] = useState<NeedConflict | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [idError, setIdError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setDraft = (next: Draft) => {
    setConflict(null);
    setDraftState(next);
  };

  const isNumber = draft.valueType === 'number';

  const submit = async (): Promise<void> => {
    setAttempted(true);
    setBannerError(null);
    setIdError(null);

    const { groupId, valueType, frequency } = draft;

    if (
      draft.label.trim() === '' ||
      groupId === '' ||
      valueType === '' ||
      frequency === '' ||
      draft.categories.length === 0
    ) {
      setBannerError(tErrors(INCOMPLETE_CODE));

      return;
    }

    if (!NEW_FACT_KEY_ID_PATTERN.test(draft.key.trim())) {
      setIdError(tErrors(INVALID_ID_CODE));

      return;
    }

    const duplicate = duplicateOf(draft.needs);

    if (duplicate !== null) {
      setConflict(duplicate);

      return;
    }

    setSaving(true);

    try {
      const unit = draft.unit.trim();
      const result = await apiClient.adminFactKeys.create({
        body: {
          key: draft.key.trim(),
          label: draft.label.trim(),
          groupId,
          valueType,
          frequency,
          categories: [...draft.categories],
          needs: normaliseNeedRows(draft.needs.map(withoutId)),
          numberStyle: isNumber ? draft.numberStyle : null,
          unit: isNumber && unit !== '' ? unit : null,
        },
      });

      if (result.ok) {
        onCreated();

        return;
      }

      const error = (result.body as { error?: { code?: string; details?: AmbiguousDetails } } | undefined)?.error;
      const code = error?.code;
      const refused =
        code === NEED_AMBIGUOUS_CODE && error?.details !== undefined
          ? conflictOf(error.details, draft.needs, [])
          : null;

      if (refused !== null) setConflict(refused);
      else if (code === INVALID_ID_CODE || code === ID_TAKEN_CODE) setIdError(tErrors(code));
      else if (code === INCOMPLETE_CODE) setBannerError(tErrors(code));
      else setBannerError(t('save-error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('create.title')}
      footer={(requestClose) => (
        <>
          <Button type="button" variant="ghost" onClick={requestClose}>
            {t('create.cancel')}
          </Button>
          <Button type="button" disabled={saving} onClick={() => void submit()}>
            {t('create.submit')}
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        {bannerError !== null && <InfoBox type="danger">{bannerError}</InfoBox>}

        <FormField id="create-fact-key-id" label={tCol('key')} error={idError}>
          {(errorId) => (
            <>
              <Input
                id="create-fact-key-id"
                className="font-mono"
                placeholder="FLA.WOHNFLAECHE"
                value={draft.key}
                invalid={idError !== null}
                aria-describedby={errorId ?? ID_HINT_ID}
                onChange={(event) => {
                  setDraft({ ...draft, key: event.target.value });
                  setIdError(null);
                }}
              />
              {idError === null && (
                <p id={ID_HINT_ID} className="text-xs text-text-tertiary">
                  {t('create.id-hint')}
                </p>
              )}
            </>
          )}
        </FormField>

        <Field label={tCol('label')} htmlFor="create-fact-key-label">
          <Input
            id="create-fact-key-label"
            value={draft.label}
            invalid={attempted && draft.label.trim() === ''}
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          />
        </Field>

        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-4">
            <Field label={tCol('group')} htmlFor="create-fact-key-group">
              <NativeSelect
                id="create-fact-key-group"
                placeholder={t('create.placeholder')}
                value={draft.groupId}
                invalid={attempted && draft.groupId === ''}
                onChange={(value) => setDraft({ ...draft, groupId: value as FactKeyGroup })}
                options={FACT_KEY_GROUPS.map((group) => ({ value: group, label: t(`group.${group}`) }))}
              />
            </Field>
            <Field label={tCol('value-type')} htmlFor="create-fact-key-value-type">
              <NativeSelect
                id="create-fact-key-value-type"
                placeholder={t('create.placeholder')}
                value={draft.valueType}
                invalid={attempted && draft.valueType === ''}
                onChange={(value) => setDraft({ ...draft, valueType: value as FactValueType })}
                options={FactValueTypeSchema.options.map((type) => ({ value: type, label: t(`value-type.${type}`) }))}
              />
            </Field>
            <Field label={tCol('frequency')} htmlFor="create-fact-key-frequency">
              <NativeSelect
                id="create-fact-key-frequency"
                placeholder={t('create.placeholder')}
                value={draft.frequency}
                invalid={attempted && draft.frequency === ''}
                onChange={(value) => setDraft({ ...draft, frequency: value as FactKeyFrequency })}
                options={FACT_KEY_FREQUENCIES.map((frequency) => ({
                  value: frequency,
                  label: t(`frequency.${frequency}`),
                }))}
              />
            </Field>
          </div>
          <p className="text-xs text-text-tertiary">{t('create.immutable-hint')}</p>
        </div>

        {isNumber && (
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('entry.number-style')} htmlFor="create-fact-key-number-style">
              <NativeSelect
                id="create-fact-key-number-style"
                value={draft.numberStyle}
                onChange={(value) => setDraft({ ...draft, numberStyle: value as NumberStyle })}
                options={DOSSIER_NUMBER_STYLES.map((style) => ({ value: style, label: t(`number-style.${style}`) }))}
              />
            </Field>
            <Field label={t('entry.unit')} htmlFor="create-fact-key-unit">
              <Input
                id="create-fact-key-unit"
                maxLength={UNIT_MAX_LENGTH}
                value={draft.unit}
                onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
              />
            </Field>
          </div>
        )}

        <FactKeyCategoryPicker
          value={draft.categories}
          invalid={attempted && draft.categories.length === 0}
          onChange={(categories) =>
            setDraft({
              ...draft,
              categories,
              needs: draft.needs.filter((row) => categories.includes(row.category)),
            })
          }
        />

        {draft.categories.length > 0 && (
          <Field label={tCol('needs')}>
            <FactKeyNeedRows
              categories={draft.categories}
              rows={draft.needs}
              conflict={conflict}
              onChange={(needs) => setDraft({ ...draft, needs })}
            />
          </Field>
        )}
      </div>
    </Modal>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly htmlFor?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="space-y-1 text-sm">
      {htmlFor === undefined ? (
        <div className="text-text-tertiary">{label}</div>
      ) : (
        <label htmlFor={htmlFor} className="block text-text-tertiary">
          {label}
        </label>
      )}
      {children}
    </div>
  );
}
